import React from 'react';
import { Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatMoneyPl } from '../../../utils/currency';
import { formatLocalizedNumber, formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { trailerTone, type TrailerLoad, type TrailerSegment } from '../transportTrailerLoad';
import './TransportTrailerView.css';

const { Text } = Typography;
const MIN_SEGMENT_SHARE = 0.12;

interface Props {
  load: TrailerLoad;
  showCosts: boolean;
}

/**
 * Trailer picture on the truck card (owner 2026-10-06, variant A): stops from
 * the doors, each segment's width by its weight against the 22 800 kg limit,
 * the free rest as „wolne”, and the payload against the limit.
 * Display only — nothing here blocks a save.
 */
export const TransportTrailerView: React.FC<Props> = ({ load, showCosts }) => {
  const { t, i18n } = useTranslation(['transport']);
  const kg = (value: number) => formatLocalizedNumber(value, 0, i18n.language);

  // Segment width = weight share of the payload; an unknown or tiny weight
  // still gets a readable minimum, the free part takes what is left.
  const shares = load.segments.map((segment) => Math.max(segment.weightKg / load.capacityKg, MIN_SEGMENT_SHARE));
  // The free part is shown with its kg; capped so the stops stay readable.
  const freeShare = Math.min(0.3, Math.max(0, 1 - shares.reduce((sum, share) => sum + share, 0)));
  const percent = Math.round((load.loadedKg / load.capacityKg) * 100);
  // Goods without a piece weight: never „0 kg / 0%” — unknown when nothing weighs,
  // a lower bound („≥”, free space „≤”) when some goods do (same rule as the form).
  const weightIncomplete = load.missingWeightNames.length > 0;
  const weightUnknown = weightIncomplete && !(load.loadedKg > 0);
  const atLeast = weightIncomplete ? '≥ ' : '';
  const atMost = weightIncomplete ? '≤ ' : '';
  const weightText = (segment: TrailerSegment) => (segment.weightComplete || segment.weightKg > 0
    ? `${segment.weightComplete ? '' : '≥ '}${kg(segment.weightKg)} kg`
    : t('transport:trailer.noWeight'));
  // One amount, one notation: the same money formatter as the „Ładunek” table (review 2026-10-06).
  const freightText = (segment: TrailerSegment) => (segment.uncovered && !segment.freightPLN
    ? t(segment.uncoveredReason === 'unpaired' ? 'transport:trailer.noPair'
      : segment.uncoveredReason === 'exceeds' ? 'transport:trailer.exceeds' : 'transport:trailer.noSupplierOrder')
    : segment.freightPLN === null ? '—'
      : formatMoneyPl(segment.freightPLN, 'PLN'));
  // Everything the narrow segment leaves out: the order, address and all goods.
  const segmentTip = (segment: TrailerSegment) => (
    <div className="transport-trailer__tip">
      <strong>{segment.kind === 'stock' ? t(segment.surplus ? 'transport:trailer.surplusToStock' : 'transport:trailer.toStock') : `${segment.orderNumber || ''} · ${segment.customerName || '—'}`}</strong>
      {segment.deliveryAddress ? <span>{t('transport:trailer.tipAddress', { address: segment.deliveryAddress })}</span> : null}
      {segment.goods.map((goods, index) => (
        <span key={`${goods.name}:${goods.dimensions ?? ''}:${index}`} data-testid="transport-trailer-tip-goods">
          {goods.name}{goods.dimensions ? ` · ${goods.dimensions}` : ''}{goods.quantity ? ` · ${formatLocalizedQuantity(goods.quantity.value, i18n.language)} ${formatUnit(goods.quantity.unit, i18n.language)}` : ''}
        </span>
      ))}
      <span>{t('transport:trailer.tipWeight', { kg: weightText(segment) })}</span>
      {showCosts ? <span>{t('transport:trailer.tipFreight', { amount: freightText(segment) })}</span> : null}
    </div>
  );

  return (
    <div className="transport-trailer" data-testid="transport-trailer">
      <div className="transport-trailer__truck" role="img" aria-label={t('transport:trailer.aria')}>
        <div className="transport-trailer__rig">
          <div className="transport-trailer__body">
            {load.segments.map((segment, index) => (
              <Tooltip key={segment.key} title={segmentTip(segment)} placement="top">
                <div
                  className={`transport-trailer__segment transport-tone--${trailerTone(segment)}`}
                  style={{ flexGrow: shares[index] }}
                  data-testid="transport-trailer-segment"
                  tabIndex={0}
                >
                  <strong className="transport-trailer__who">
                    {segment.kind === 'stock'
                      ? t(segment.surplus ? 'transport:trailer.surplusShort' : 'transport:trailer.toStock')
                      : `${segment.stop ?? '–'}. ${segment.customerName || segment.orderNumber || '—'}`}
                  </strong>
                  <span className="transport-trailer__weight">{weightText(segment)}</span>
                  {showCosts ? <span className="transport-trailer__freight">{freightText(segment)}</span> : null}
                </div>
              </Tooltip>
            ))}
            {freeShare > 0.02 ? (
              <div className="transport-trailer__segment transport-trailer__segment--free" style={{ flexGrow: freeShare }}>
                <span>{t('transport:trailer.free')}</span>
                {weightUnknown ? null : <strong>{atMost}{kg(Math.max(load.remainingKg, 0))} kg</strong>}
              </div>
            ) : null}
          </div>
          <div className="transport-trailer__hitch" aria-hidden="true" />
          <div className="transport-trailer__cab" aria-hidden="true"><i /></div>
        </div>
        <div className="transport-trailer__wheels" aria-hidden="true">
          <span className="transport-trailer__axles"><i /><i /><i /></span>
          <span className="transport-trailer__axles transport-trailer__axles--cab"><i /><i /></span>
        </div>
      </div>

      <div className="transport-trailer__capacity">
        {weightUnknown ? null : (
          <div className="transport-trailer__bar"><i className={load.overCapacity ? 'is-over' : ''} style={{ width: `${Math.min(percent, 100)}%` }} /></div>
        )}
        <Text className={load.overCapacity ? 'transport-trailer__over' : undefined} data-testid="transport-trailer-capacity">
          {weightUnknown ? t('transport:trailer.loadUnknown', { capacity: kg(load.capacityKg) }) : (
            <>
              {t('transport:trailer.load', { loaded: `${atLeast}${kg(load.loadedKg)}`, capacity: kg(load.capacityKg), percent: `${atLeast}${percent}` })}
              {' · '}
              {load.overCapacity
                ? t('transport:trailer.over', { kg: `${atLeast}${kg(-load.remainingKg)}` })
                : t('transport:trailer.remaining', { kg: `${atMost}${kg(load.remainingKg)}` })}
            </>
          )}
        </Text>
      </div>
      {load.missingWeightNames.length > 0 ? (
        <Text type="warning" className="transport-trailer__note">
          {t('transport:trailer.missingWeight', { names: load.missingWeightNames.join(', ') })}
        </Text>
      ) : null}

    </div>
  );
};
